import { useState } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, RefreshCw } from "lucide-react";
import ChapterHeading from "@/components/landing/ChapterHeading";
import { buildWeeks, toKey, MONTH_NAMES, DAY_NAMES } from "@/lib/dates";

export const SOURCE_COLORS = {
  booking: "#003580",
  airbnb: "#FF5A5F",
  bnb: "#D97706",
  direct: "#15803D",
};

export const SOURCE_NAMES = {
  booking: "Booking.com",
  airbnb: "Airbnb",
  bnb: "Bed-and-Breakfast.it",
  direct: "Prenotazione diretta",
};

function Month({ year, month, busyMap, range, onSelect, todayKey }) {
  const weeks = buildWeeks(year, month);
  return (
    <div className="w-full">
      <p className="mb-4 text-center font-serif text-xl text-stone-800">
        {MONTH_NAMES[month]} <span className="text-stone-400">{year}</span>
      </p>
      <div className="mb-2 grid grid-cols-7 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-stone-400">
        {DAY_NAMES.map((d) => (
          <span key={d}>{d}</span>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {weeks.flat().map((day, i) => {
          if (!day) return <span key={`e-${i}`} />;
          const key = toKey(day);
          const busy = busyMap.get(key);
          const isPast = key < todayKey;
          const isFrom = range.from && toKey(range.from) === key;
          const isTo = range.to && toKey(range.to) === key;
          const inRange = range.from && range.to && day > range.from && day < range.to;
          return (
            <button
              key={key}
              type="button"
              data-testid={`cal-day-${key}`}
              disabled={isPast || !!busy}
              onClick={() => onSelect(day)}
              className={`relative flex aspect-square flex-col items-center justify-center rounded-sm text-sm transition-colors duration-200
                ${isPast ? "text-stone-300" : busy ? "cursor-not-allowed text-stone-300 line-through" : "text-stone-700 hover:bg-terracotta/10"}
                ${inRange ? "bg-terracotta/15" : ""}
                ${isFrom || isTo ? "bg-terracotta !text-linen" : ""}`}
            >
              {day.getDate()}
              {busy && (
                <span
                  className="absolute bottom-1 h-1 w-5 rounded-full"
                  style={{ backgroundColor: SOURCE_COLORS[busy] }}
                />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function Disponibilita({ busyMap, loading, updatedAt, range, onSelect }) {
  const now = new Date();
  const [offset, setOffset] = useState(0);
  const base = new Date(now.getFullYear(), now.getMonth() + offset, 1);
  const second = new Date(now.getFullYear(), now.getMonth() + offset + 1, 1);
  const todayKey = toKey(now);

  return (
    <section id="disponibilita" data-testid="availability-section" className="bg-[#EEF1E6] py-24 sm:py-36">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <ChapterHeading
          eyebrow="Sempre aggiornato"
          title="Calendario delle Disponibilità"
          subtitle="Sincronizzato in tempo reale con i calendari di Booking.com, Airbnb e Bed-and-Breakfast.it. Seleziona le date libere e invia la richiesta."
        />

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="rounded-sm border border-stone-300/50 bg-white p-6 shadow-xl shadow-stone-900/5 sm:p-10"
        >
          <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <button
                data-testid="cal-prev-month"
                onClick={() => setOffset(Math.max(0, offset - 1))}
                disabled={offset === 0}
                className="flex h-9 w-9 items-center justify-center rounded-full border border-stone-300 text-stone-600 transition-colors hover:bg-stone-100 disabled:opacity-30"
                aria-label="Mese precedente"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                data-testid="cal-next-month"
                onClick={() => setOffset(offset + 1)}
                disabled={offset >= 11}
                className="flex h-9 w-9 items-center justify-center rounded-full border border-stone-300 text-stone-600 transition-colors hover:bg-stone-100 disabled:opacity-30"
                aria-label="Mese successivo"
              >
                <ChevronRight size={16} />
              </button>
            </div>
            <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.15em] text-stone-400" data-testid="cal-updated-at">
              <RefreshCw size={11} className={loading ? "animate-spin" : ""} />
              {loading
                ? "Sincronizzazione in corso…"
                : updatedAt
                  ? `Aggiornato ${new Intl.DateTimeFormat("it-IT", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }).format(new Date(updatedAt))}`
                  : ""}
            </p>
          </div>

          <div className="grid gap-10 md:grid-cols-2">
            <Month year={base.getFullYear()} month={base.getMonth()} busyMap={busyMap} range={range} onSelect={onSelect} todayKey={todayKey} />
            <Month year={second.getFullYear()} month={second.getMonth()} busyMap={busyMap} range={range} onSelect={onSelect} todayKey={todayKey} />
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 border-t border-stone-200 pt-6" data-testid="cal-legend">
            {Object.entries(SOURCE_NAMES).map(([id, name]) => (
              <span key={id} className="flex items-center gap-2 text-xs text-stone-600" data-testid={`legend-${id}`}>
                <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: SOURCE_COLORS[id] }} />
                {name}
              </span>
            ))}
            <span className="ml-auto text-xs italic text-stone-400">I giorni barrati sono già prenotati</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
